import React from 'react';
import PropTypes from 'prop-types';
import Color from 'color';

import { css, keyframes } from 'react-emotion';

import { baselineGrid, colors, animation, zIndex } from '../utils/presets';
import { toRem, toVW, mqObj as mq } from '../utils/functions';
import { options } from '../utils/typography';

// Import components
import InnerWrapper from '../components/inner-wrapper';
import Button from '../components/button';
import BottomShape from '../assets/svg-sprite/shape-hero-curved-bottom.svg';

const { curve, speed } = animation;

const bgColor = Color(colors.blue).hsl();
const cornerColor = bgColor.rotate(32).lighten(0.2);

const cover = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const hero = css`
  margin-bottom: ${toRem(baselineGrid * 6)};

  ${mq.lap} {
    margin-bottom: ${toRem(baselineGrid * 8)};
  }

  ${mq.desk} {
    margin-bottom: ${toRem(baselineGrid * 10)};
  }
`;

const heroWrapper = css`
  position: relative;
`;

const heroInside = css`
  position: relative;
  overflow: hidden;
  padding-bottom: ${176 / 1220 * 100}%;
  background-color: ${bgColor.string()};
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const heroImageWrapper = css`
  ${cover};
  z-index: ${zIndex[0]};
  animation: ${fadeIn} ${speed.slow} ${curve.default};

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    background-image: linear-gradient(
      to top right,
      ${bgColor.alpha(0).string()} 56%,
      ${cornerColor.string()}
    );
    z-index: ${zIndex[1]};
    pointer-events: none;
    opacity: 0.8;

    ${mq.lap} {
      width: 50%;
      padding-bottom: 50%;
    }
  }
`;

const heroImage = css`
  width: 100%;
  height: auto;
  opacity: 0.35;
  transform: scale(1.03);

  @supports (object-fit: cover) {
    width: 100%;
    height: 100%;
    margin: 0;
    object-fit: cover;
    object-position: top;
  }
`;

const heroPlug = css`
  position: relative;
  z-index: ${zIndex[1]};
  padding: 10% 12%;
  font-family: ${options.headerFontFamily.join(',')};
  font-size: calc(${toRem(baselineGrid * 3)} + ${toVW(baselineGrid * 4)});
  line-height: 1.2;
  color: ${colors.ui.bright};
`;

const heroBottom = css`
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 100.44%;
  transform: translate(-50%, 0);
  padding-bottom: ${232 / 1220 * 100}%;
  z-index: ${zIndex[2]};
`;

const heroBottomShape = css`
  ${cover};
  display: block;
  fill: ${colors.ui.bright};
  pointer-events: none;
`;

const heroCTAWrapper = css`
  position: relative;

  ${mq.lapLarge} {
    margin-top: -3.28%;
  }
`;

const heroCTA = css`
  position: relative;
  margin-top: ${toRem(baselineGrid * 2)};
  z-index: ${zIndex[3]};
  text-align: center;

  ${mq.palm} {
    padding: 0 12%;
    text-align: left;
  }

  @media (min-width: 480px) {
    margin-top: ${toRem(baselineGrid)};
  }

  ${mq.lap} {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    margin-top: 0;
  }
`;

const heroButton = css`
  margin: 0;
  font-size: ${toRem(16)};
  white-space: nowrap;

  ${mq.lapLarge} {
    font-size: ${toRem(18)};
  }
`;

const Hero = ({ image, plug }) => (
  <section className={hero}>
    <InnerWrapper className={heroWrapper} narrow>
      <div className={heroInside}>
        <div className={heroImageWrapper}>
          <img
            className={heroImage}
            src={image.childImageSharp.sizes.src}
            srcSet={image.childImageSharp.sizes.srcSet}
            sizes={image.childImageSharp.sizes.sizes}
            alt=""
          />
        </div>
        <div className={heroPlug}>{plug}</div>
        <div className={heroBottom}>
          <svg className={heroBottomShape} viewBox={BottomShape.viewBox}>
            <use xlinkHref={`#${BottomShape.id}`} />
          </svg>
        </div>
      </div>
      <div className={heroCTAWrapper}>
        <div className={heroCTA}>
          <Button
            to={'/tumblr-themes/'}
            className={heroButton}
            backgroundColor={colors.yellow}
            shadow
            arrow
          >
            {'Choose your theme'}
          </Button>
        </div>
      </div>
    </InnerWrapper>
  </section>
);

Hero.propTypes = {
  image: PropTypes.object.isRequired,
  plug: PropTypes.string.isRequired,
};

export default Hero;

export const heroImageFragment = graphql`
  fragment heroImage on File {
    childImageSharp {
      sizes(maxWidth: 1280, toFormat: JPG, quality: 80) {
        ...GatsbyImageSharpSizes
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/hero.js